/**
 * Global walk-in request modal store (2026-09-16, Jaime feature — time
 * sensitive). Mirrors the planLimitModal.ts pub/sub pattern: a small
 * module-level store that the AppShell notification polling and the
 * Notifications UI both feed; a single app-level <WalkInModal/> (mounted once
 * in App.tsx, next to <PlanLimitModal/>) subscribes and renders.
 *
 * Behavior:
 * - Only fires for NEW unread walk_in_request notifications that were not
 *   present in the previous snapshot (session-lifetime memory; a dismissed
 *   popup never re-pops, but the badge + /notifications still surface it).
 * - One modal at a time: if a popup is already open, the newest request
 *   replaces it in place (no stacking).
 * - Modal only appears from the existing 30s poll / focus refresh — same
 *   visibility rules as the polling itself (no push).
 */

export interface WalkInModalInfo {
  /** Notification row id — used for de-dupe and post-decision badge refresh. */
  notificationId: string;
  /** access_codes.id — the id POST /walk-ins/:id/approve|deny expects. */
  walkInId: string;
  visitorName: string;
  unitLabel?: string | null;
  detail?: string | null;
  receivedAt?: string | null;
}

type Listener = (info: WalkInModalInfo | null) => void;

let current: WalkInModalInfo | null = null;
const listeners = new Set<Listener>();
/** Notification ids already surfaced this session (dismissed or decided). */
const surfaced = new Set<string>();

function emit() {
  for (const l of listeners) l(current);
}

export function subscribeWalkInModal(listener: Listener): () => void {
  listeners.add(listener);
  listener(current); // sync initial state (e.g. remount while one is open)
  return () => { listeners.delete(listener); };
}

/** Open/update the global walk-in modal. Newest request wins; never stacks. */
export function showWalkInModal(info: WalkInModalInfo): void {
  if (surfaced.has(info.notificationId)) return; // dismissed/decided already
  surfaced.add(info.notificationId);
  current = info; // update in place if one was open
  emit();
}

export function closeWalkInModal(): void {
  if (current) surfaced.add(current.notificationId);
  current = null;
  emit();
}

/** Post-decision close (Aprobar/Rechazado feedback shown first). */
export function resolveWalkInModal(): void {
  if (current) surfaced.add(current.notificationId);
  current = null;
  emit();
}

// --- Notification-payload helpers -------------------------------------

/** Parse the notification `data` blob (may be JSON-encoded or an object). */
function parseData(raw: unknown): Record<string, any> | null {
  if (!raw) return null;
  let d: any = raw;
  if (typeof d === 'string') {
    try { d = JSON.parse(d); } catch { return null; }
  }
  return d && typeof d === 'object' ? d : null;
}

/** access_codes.id carried by walk-in notifications (same as getWalkInId in
 *  Notifications.tsx — duplicated here to keep the store dependency-free). */
export function walkInIdFromNotification(n: any): string | null {
  const d = parseData((n as any)?.data);
  const id = d?.access_code_id || d?.id || null;
  return id ? String(id) : null;
}

/**
 * Feed from any notification list snapshot (AppShell poll, Notifications UI).
 * Detects NEW unread walk_in_request rows that were not in the previous
 * snapshot and opens/updates the app-level modal for the newest one.
 * Returns the list of newly surfaced ids (for callers that log/report).
 */
export function surfaceNewWalkInRequests(items: any[]): string[] {
  const list = Array.isArray(items) ? items : [];
  // BUG FIX 2026-09-23: only surface requests still inside the 15-minute
  // decision window. A stale unread walk_in_request (e.g. one whose access
  // code was already swept to WALK_IN_TIMEOUT before the notification was
  // marked read) must never pop the approval modal on app open.
  const PENDING_WINDOW_MS = 20 * 60 * 1000; // 15-min backend window + slack
  const fresh = list.filter((n) => {
    if (n?.type !== 'walk_in_request' || n?.read || surfaced.has(String(n.id))) return false;
    const ts = Date.parse(n.timestamp || n.created_at || '');
    if (Number.isFinite(ts) && Date.now() - ts > PENDING_WINDOW_MS) return false;
    return true;
  });
  const surfacedIds: string[] = [];
  for (const n of fresh) {
    const walkInId = walkInIdFromNotification(n);
    if (!walkInId) continue; // cannot act without the id
    const d = parseData(n.data);
    surfacedIds.push(String(n.id));
    showWalkInModal({
      notificationId: String(n.id),
      walkInId,
      visitorName: String(d?.visitor_name || n.title || 'Visitante'),
      unitLabel: d?.unit_label || d?.unitLabel || d?.unit || null,
      detail: n.message || null,
      receivedAt: n.timestamp || n.created_at || null,
    });
  }
  return surfacedIds;
}
